import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, CheckCircle2 } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Combobox } from '@/components/Combobox';
import { Skeleton } from '@/components/EmptyState';
import { api } from '@/lib/api';
import { toast } from '@/components/Toaster';

interface AssetDefault {
  asset_category_id: string;
  company_id?: string;
  default_fixed_asset_account_id?: string;
  default_accumulated_depr_id?: string;
  default_depreciation_expense_id?: string;
}
interface AssetCategory { id: string; name: string; }
interface Account       { id: string; name: string; account_number?: string; account_type?: string; }

/**
 * Settings → Asset Defaults
 *
 * Per-(asset category, active company) account overrides applied when an
 * Asset is created without explicit accounts. Mirrors the ERPNext defaults
 * pattern — one row per category, each with the three depreciation accounts.
 */
export function AssetDefaultsSection() {
  const qc = useQueryClient();

  const { data: catsResp,      isLoading: catsLoading } = useQuery({ queryKey: ['asset-categories'], queryFn: () => api<{ items: AssetCategory[] }>('/assets/asset-categories') });
  const { data: accountsResp                          } = useQuery({ queryKey: ['accounts'],         queryFn: () => api<{ items: Account[] }>('/accounting/accounts') });
  const { data: defaultsResp,  isLoading: defsLoading } = useQuery({ queryKey: ['asset-defaults'],   queryFn: () => api<{ items: AssetDefault[] }>('/assets/asset-defaults') });

  const categories = catsResp?.items ?? [];
  const accounts   = accountsResp?.items ?? [];

  // Build a working copy keyed by category — drop existing rows on top of
  // empty stubs so every category renders even if no defaults exist yet.
  const [draft, setDraft] = useState<Record<string, AssetDefault>>({});
  useEffect(() => {
    const next: Record<string, AssetDefault> = {};
    for (const c of categories) next[c.id] = { asset_category_id: c.id };
    for (const d of defaultsResp?.items ?? []) next[d.asset_category_id] = { ...next[d.asset_category_id], ...d };
    setDraft(next);
  }, [categories, defaultsResp]);

  const accountOptions = useMemo(
    () => accounts.map((a) => ({
      value: a.id, label: a.name,
      description: [a.account_number, a.account_type].filter(Boolean).join(' · '),
    })),
    [accounts],
  );

  const saveMutation = useMutation({
    mutationFn: (row: AssetDefault) =>
      api<AssetDefault>('/assets/asset-defaults', { method: 'PUT', body: row }),
    onSuccess: () => {
      toast.success('Saved', 'Asset defaults updated.');
      void qc.invalidateQueries({ queryKey: ['asset-defaults'] });
    },
    onError: (e: { message?: string }) => toast.error('Save failed', e?.message ?? 'See API logs'),
  });

  function setVal(catID: string, key: keyof AssetDefault, val: string) {
    setDraft((d) => ({
      ...d,
      [catID]: { ...d[catID], asset_category_id: catID, [key]: val },
    }));
  }

  const isLoading = catsLoading || defsLoading;

  return (
    <div className="space-y-4 max-w-6xl">
      <Card>
        <CardTitle>Asset Defaults</CardTitle>
        <CardDescription>
          Per-category fallback accounts applied when an Asset is created without explicit
          choices. Leave any field blank to inherit from the parent Asset Category.
        </CardDescription>
      </Card>

      {isLoading ? (
        <Card><Skeleton className="h-32 w-full" /></Card>
      ) : categories.length === 0 ? (
        <Card className="!p-8 text-center">
          <CardTitle>No asset categories yet</CardTitle>
          <CardDescription>Create an Asset Category first; defaults attach per category.</CardDescription>
        </Card>
      ) : (
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                <tr>
                  <th className="text-left font-medium px-4 py-2 min-w-[150px]">Category</th>
                  <th className="text-left font-medium px-3 py-2 min-w-[190px]">Fixed asset account</th>
                  <th className="text-left font-medium px-3 py-2 min-w-[190px]">Accumulated depreciation</th>
                  <th className="text-left font-medium px-3 py-2 min-w-[190px]">Depreciation expense</th>
                  <th className="px-3 py-2 w-24" />
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => {
                  const row = draft[c.id] ?? { asset_category_id: c.id };
                  const dirty = saveMutation.variables?.asset_category_id === c.id && saveMutation.isPending;
                  return (
                    <tr key={c.id} className="border-t border-hairline">
                      <td className="px-4 py-2 text-charcoal font-medium">{c.name}</td>
                      <td className="px-2 py-1.5">
                        <Combobox
                          options={accountOptions}
                          value={row.default_fixed_asset_account_id ?? null}
                          onChange={(v) => setVal(c.id, 'default_fixed_asset_account_id', v ?? '')}
                          placeholder="— None —" clearable
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Combobox
                          options={accountOptions}
                          value={row.default_accumulated_depr_id ?? null}
                          onChange={(v) => setVal(c.id, 'default_accumulated_depr_id', v ?? '')}
                          placeholder="— None —" clearable
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Combobox
                          options={accountOptions}
                          value={row.default_depreciation_expense_id ?? null}
                          onChange={(v) => setVal(c.id, 'default_depreciation_expense_id', v ?? '')}
                          placeholder="— None —" clearable
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="secondary" loading={dirty} onClick={() => saveMutation.mutate(row)}>
                          <Save className="size-3.5" /> Save
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 sm:px-5 py-3 border-t border-hairline flex items-center gap-2 text-caption text-stone">
            <CheckCircle2 className="size-3.5" /> Each row saves independently. Inherits from Asset Category when a field is blank.
          </div>
        </Card>
      )}
    </div>
  );
}
